'use client'

// Fences — one shared wire-fence panel (fence.glb: Wire3 alpha-mask + Metal_16
// frame), GPU-instanced at the transforms in sceneConfig.FENCES via drei
// <Instances> — the same data-driven idiom as the tree trunks. Positions +
// rotations live in config so the layout is editable without touching a GLB.
// The panel has two submeshes/materials, so we spin up one <Instances> group
// per material; each renders all FENCES in a single draw call.

import { useMemo } from 'react'
import { Instances, Instance } from '@react-three/drei'
import * as THREE from 'three/webgpu'
import { useModel } from '../hooks/useModel'
import { FENCES } from '../../../lib/world/sceneConfig'

const MODEL = '/models/fence.glb'

export function Fences() {
  const { scene } = useModel(MODEL)

  // Pull the two submeshes (wire + metal) and convert their materials to
  // MeshStandardNodeMaterial so they light, shadow, and dither with the world.
  const parts = useMemo(() => {
    const out = []
    const converted = new Map()
    scene.traverse((o) => {
      if (!o.isMesh) return
      const src = o.material
      if (!src || Array.isArray(src)) return
      const isWire = /wire/i.test(src.name || '')
      let nm = converted.get(src)
      if (!nm) {
        nm = new THREE.MeshStandardNodeMaterial()
        if (src.map) nm.map = src.map
        nm.color = src.color?.clone() ?? new THREE.Color('white')
        nm.roughness = src.roughness ?? 1
        nm.metalness = src.metalness ?? 0
        nm.side = THREE.DoubleSide // both panels are double-sided
        nm.vertexColors = src.vertexColors
        if (isWire) {
          nm.alphaTest = 0.1 // baseColor alpha = wire mask → hard cutout
          nm.transparent = false
        }
        converted.set(src, nm)
      }
      out.push({ key: o.uuid, geometry: o.geometry, material: nm, isWire })
    })
    return out
  }, [scene])

  return (
    <>
      {parts.map((p) => (
        <Instances
          key={p.key}
          geometry={p.geometry}
          material={p.material}
          castShadow={!p.isWire} // wire cutout would cast an ugly solid rect
          receiveShadow
          limit={FENCES.length}
        >
          {FENCES.map((f) => (
            <Instance key={f.id} position={f.position} rotation={f.rotation} />
          ))}
        </Instances>
      ))}
    </>
  )
}

export default Fences
